import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import { showError, showSuccess } from '@/app/store/toastSlice';
import { enablePush, getPushAvailability } from '@/shared/push';
import { BottomSheet, bottomSheetStyles } from '../BottomSheet/BottomSheet';
import {
  isCookieNoticeAcknowledged,
  onCookieNoticeAcknowledged,
} from '../CookieNotice';
import styles from './PushPrompt.module.less';

const DISMISS_KEY = 'push_prompt_dismissed_at';
const REASK_AFTER_DAYS = 14;
const SHOW_DELAY_MS = 1500;

function wasDismissedRecently(): boolean {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const dismissedAt = Number(raw);
  if (Number.isNaN(dismissedAt)) return false;
  return Date.now() - dismissedAt < REASK_AFTER_DAYS * 24 * 60 * 60 * 1000;
}

/**
 * Предложение включить пуши: если разрешение ещё не спрашивали и шторку
 * не отклоняли 14 дней. Тап по «Включить» — user gesture, нужный iOS Safari.
 * Пока не закрыто уведомление о cookie, ждёт его.
 */
export function PushPrompt() {
  const dispatch = useAppDispatch();
  const isLoggedIn = useAppSelector((state) => state.login.isLoggedIn);

  const [isOpen, setIsOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isLoggedIn || wasDismissedRecently()) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const schedule = () => {
      timer = setTimeout(async () => {
        const availability = await getPushAvailability();

        if (!cancelled && availability === 'available') setIsOpen(true);
      }, SHOW_DELAY_MS);
    };

    const unsubscribe = isCookieNoticeAcknowledged()
      ? undefined
      : onCookieNoticeAcknowledged(schedule);
    if (!unsubscribe) schedule();

    return () => {
      cancelled = true;
      clearTimeout(timer);
      unsubscribe?.();
    };
  }, [isLoggedIn]);

  /** Отказ от шторки: спрашиваем не раньше, чем через REASK_AFTER_DAYS */
  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setIsOpen(false);
  };

  const handleEnable = async () => {
    setBusy(true);
    try {
      // Ждём только ответа на системный запрос: подписка дооформляется в фоне
      if (await enablePush(dispatch)) {
        dispatch(showSuccess('Уведомления включены'));
      }
    } catch (err) {
      console.error('[enablePush]', err);
      dispatch(showError('Не удалось включить уведомления'));
    } finally {
      setBusy(false);
      // Метка не нужна: и разрешение, и запрет уводят availability из available
      setIsOpen(false);
    }
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={dismiss} variant="actions">
      <div className={bottomSheetStyles.group}>
        <div className={styles.body}>
          <h3 className={styles.title}>Включите уведомления</h3>
          <p className={styles.message}>
            Будем сообщать о новых электронных рецептах и о статусе заказов
          </p>
        </div>
        <button
          type="button"
          className={styles.action}
          onClick={handleEnable}
          disabled={busy}
        >
          {busy ? 'Включаем…' : 'Включить'}
        </button>
      </div>

      <button
        type="button"
        className={`${bottomSheetStyles.group} ${styles.dismiss}`}
        onClick={dismiss}
        disabled={busy}
      >
        Не сейчас
      </button>
    </BottomSheet>
  );
}
